<script>
  import Icon from "@iconify/svelte";
  import { onMount } from "svelte";
  import Layout from "../components/Layout.svelte";
  import SlideOver from "../components/SlideOver.svelte";
  import { t } from "../lib/i18n.svelte.js";
  import { api } from "../lib/api.svelte.js";
  import { addToast } from "../components/Toast.svelte";

  // ---- state ----
  let currentPath = $state("");
  let items = $state([]);
  let loading = $state(true);
  let loadingItems = $state(false);
  let error = $state("");
  let uploading = $state(false);
  let viewMode = $state(localStorage.getItem("snap_storage_view") || "list");
  let dragOver = $state(false);

  // Drawer state
  let drawerItem = $state(null);

  // Delete modal
  let deleteTarget = $state(null);
  let deleting = $state(false);

  // File input ref
  let fileInput = $state(null);

  // ---- derived ----
  let breadcrumbs = $derived.by(() => {
    if (!currentPath) return [];
    return currentPath.split("/").filter(Boolean);
  });

  let sortedItems = $derived(
    [...items].sort((a, b) => {
      if (a.is_dir && !b.is_dir) return -1;
      if (!a.is_dir && b.is_dir) return 1;
      return (a.name || "").localeCompare(b.name || "");
    })
  );

  let folderCount = $derived(sortedItems.filter(i => i.is_dir).length);
  let fileCount = $derived(sortedItems.filter(i => !i.is_dir).length);

  let mainClass = $derived(
    `flex-1 min-w-0 transition-all ${drawerItem ? "lg:mr-[380px]" : ""}`
  );

  // ---- helpers ----
  function setViewMode(mode) {
    viewMode = mode;
    localStorage.setItem("snap_storage_view", mode);
  }

  function encodePath(p) {
    return p
      ? p.split("/").filter(Boolean).map(encodeURIComponent).join("/")
      : "";
  }

  function formatSize(bytes) {
    if (bytes == null || bytes === 0) return "\u2014";
    const units = ["B", "KB", "MB", "GB", "TB"];
    let i = 0;
    let size = bytes;
    while (size >= 1024 && i < units.length - 1) { size /= 1024; i++; }
    return `${size.toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
  }

  function formatDate(dateStr) {
    if (!dateStr) return "\u2014";
    try {
      return new Date(dateStr).toLocaleString();
    } catch { return dateStr; }
  }

  function fileIcon(name) {
    const ext = (name || "").split(".").pop()?.toLowerCase();
    if (["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "ico", "tiff", "tif", "avif"].includes(ext)) return "mdi:file-image-outline";
    if (["pdf"].includes(ext)) return "mdi:file-pdf-box";
    if (["doc", "docx", "odt", "rtf"].includes(ext)) return "mdi:file-word-outline";
    if (["xls", "xlsx", "ods"].includes(ext)) return "mdi:file-excel-outline";
    if (["ppt", "pptx", "odp"].includes(ext)) return "mdi:file-powerpoint-outline";
    if (["csv", "tsv"].includes(ext)) return "mdi:file-table-outline";
    if (["zip", "tar", "gz", "bz2", "xz", "rar", "7z", "tgz", "zst"].includes(ext)) return "mdi:folder-zip-outline";
    if (["mp4", "mov", "avi", "mkv", "webm", "flv", "wmv", "m4v", "mpg", "mpeg"].includes(ext)) return "mdi:file-video-outline";
    if (["mp3", "wav", "ogg", "flac", "aac", "wma", "m4a", "opus", "aiff"].includes(ext)) return "mdi:file-music-outline";
    if (["json"].includes(ext)) return "mdi:code-json";
    if (["xml", "xsl", "xslt"].includes(ext)) return "mdi:file-xml-box";
    if (["yaml", "yml", "toml", "ini", "cfg", "conf"].includes(ext)) return "mdi:file-cog-outline";
    if (["html", "htm", "xhtml"].includes(ext)) return "mdi:language-html5";
    if (["css", "scss", "sass", "less"].includes(ext)) return "mdi:language-css3";
    if (["js", "mjs", "cjs"].includes(ext)) return "mdi:language-javascript";
    if (["ts", "tsx"].includes(ext)) return "mdi:language-typescript";
    if (["py", "pyw"].includes(ext)) return "mdi:language-python";
    if (["go"].includes(ext)) return "mdi:language-go";
    if (["rs"].includes(ext)) return "mdi:language-rust";
    if (["java", "jar", "class"].includes(ext)) return "mdi:language-java";
    if (["rb"].includes(ext)) return "mdi:language-ruby";
    if (["php"].includes(ext)) return "mdi:language-php";
    if (["c", "h"].includes(ext)) return "mdi:language-c";
    if (["cpp", "cc", "cxx", "hpp"].includes(ext)) return "mdi:language-cpp";
    if (["cs"].includes(ext)) return "mdi:language-csharp";
    if (["swift"].includes(ext)) return "mdi:language-swift";
    if (["kt", "kts"].includes(ext)) return "mdi:language-kotlin";
    if (["dart"].includes(ext)) return "mdi:flutter";
    if (["sql"].includes(ext)) return "mdi:database-outline";
    if (["sh", "bash", "zsh", "fish"].includes(ext)) return "mdi:console";
    if (["dockerfile"].includes(ext) || (name || "").toLowerCase() === "dockerfile") return "mdi:docker";
    if (["txt", "log", "readme"].includes(ext)) return "mdi:file-document-outline";
    if (["md", "mdx", "markdown"].includes(ext)) return "mdi:language-markdown";
    if (["ttf", "otf", "woff", "woff2", "eot"].includes(ext)) return "mdi:format-font";
    if (["exe", "msi", "dmg", "app", "deb", "rpm", "apk"].includes(ext)) return "mdi:application-outline";
    if (["iso", "img"].includes(ext)) return "mdi:disc";
    if (["pem", "crt", "cer", "key", "p12", "pfx"].includes(ext)) return "mdi:certificate-outline";
    if (["env"].includes(ext)) return "mdi:file-lock-outline";
    if (["lock"].includes(ext)) return "mdi:file-lock-outline";
    return "mdi:file-outline";
  }

  function isImage(name) {
    const ext = (name || "").split(".").pop()?.toLowerCase();
    return ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "ico", "avif"].includes(ext);
  }

  function downloadUrl(path) {
    return `/storage/dl/${encodePath(path)}`;
  }

  // ---- API calls ----
  async function browse(path) {
    const p = (path || "").replace(/^\/+|\/+$/g, "");
    currentPath = p;
    loadingItems = true;
    drawerItem = null;
    error = "";
    try {
      const encoded = encodePath(p);
      const url = encoded ? `/storage/${encoded}` : "/storage";
      const res = await api.get(url);
      items = res?.entries || [];
    } catch (e) {
      const msg = e?.message || String(e);
      error = msg;
      items = [];
    } finally {
      loadingItems = false;
    }
  }

  function selectItem(item) {
    if (item.is_dir) {
      const next = currentPath ? `${currentPath}/${item.name}` : item.name;
      browse(next);
    } else {
      drawerItem = item;
    }
  }

  function goBack() {
    if (!currentPath) return;
    const segments = currentPath.split("/").filter(Boolean);
    if (segments.length <= 1) {
      browse("");
    } else {
      browse(segments.slice(0, -1).join("/"));
    }
  }

  function navigateBreadcrumb(index) {
    const segments = breadcrumbs;
    const path = segments.slice(0, index + 1).join("/");
    browse(path);
  }

  async function handleUpload(e) {
    const files = e.target?.files;
    if (!files || files.length === 0) return;

    uploading = true;
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        const encoded = encodePath(currentPath);
        const url = encoded ? `/storage/${encoded}` : "/storage";
        await api.post(url, formData);
      }
      addToast(t("storage_page.uploaded").replace("{count}", files.length), "success");
      await browse(currentPath);
    } catch (e) {
      const msg = e?.message || String(e);
      if (msg.includes("404")) {
        addToast(t("storage_page.upload_unavailable"), "warning");
      } else {
        addToast(msg || t("storage_page.upload_failed"), "error");
      }
    } finally {
      uploading = false;
      if (fileInput) fileInput.value = "";
    }
  }

  function handleDrop(e) {
    e.preventDefault();
    dragOver = false;
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      handleUpload({ target: { files } });
    }
  }

  function handleDragOver(e) {
    e.preventDefault();
    dragOver = true;
  }

  function confirmDelete(item) {
    deleteTarget = item;
  }

  async function handleDelete() {
    if (!deleteTarget || deleting) return;
    deleting = true;
    try {
      const key = deleteTarget.key || (currentPath ? `${currentPath}/${deleteTarget.name}` : deleteTarget.name);
      await api.delete(`/storage/${encodePath(key)}`);
      addToast(t("storage_page.deleted").replace("{name}", deleteTarget.name), "success");
      if (drawerItem?.name === deleteTarget.name) drawerItem = null;
      await browse(currentPath);
    } catch (e) {
      const msg = e?.message || String(e);
      if (msg.includes("404")) {
        addToast(t("storage_page.delete_unavailable"), "warning");
      } else {
        addToast(msg || t("storage_page.delete_failed"), "error");
      }
    } finally {
      deleting = false;
      deleteTarget = null;
    }
  }

  // ---- lifecycle ----
  onMount(async () => {
    try {
      await browse("");
    } catch {
      // handled in browse()
    } finally {
      loading = false;
    }
  });
</script>

<Layout>
  <div class="flex relative">
    <!-- MAIN CONTENT -->
    <div class={mainClass}>
      <div class="ui-card p-5 sm:p-6">
        <!-- Header -->
        <div class="flex items-start gap-3 mb-6">
          <span class="ui-header-icon">
            <Icon icon="streamline:cloud-share" class="w-[18px] h-[18px]" />
          </span>
          <div class="flex-1">
            <h1 class="text-lg font-normal tracking-wide text-zinc-100">{t("nav.storage")}</h1>
            <p class="mt-0.5 text-sm text-zinc-400">{t("storage.subtitle")}</p>
          </div>

          <!-- Upload button -->
          <button
            onclick={() => fileInput?.click()}
            disabled={uploading}
            class="ui-btn-accent !px-4 !py-2 !text-sm !gap-2 disabled:opacity-50"
          >
            {#if uploading}
              <Icon icon="mdi:loading" class="w-4 h-4 animate-spin" />
            {:else}
              <Icon icon="mdi:upload" class="w-4 h-4" />
            {/if}
            {t("storage_page.upload")}
          </button>
          <input
            bind:this={fileInput}
            type="file"
            multiple
            class="hidden"
            onchange={handleUpload}
          />
        </div>

        <!-- Breadcrumb bar -->
        <div class="flex items-center mb-4 min-h-[40px] rounded-lg px-2 bg-black/20 border border-white/10">
          <!-- Back / Up buttons -->
          <div class="flex items-center shrink-0 mr-1">
            <button
              onclick={goBack}
              disabled={!currentPath}
              class="flex h-8 w-8 items-center justify-center rounded-md transition-colors disabled:opacity-30 text-zinc-400 hover:text-white hover:bg-white/10"
              title={t("storage_page.go_back")}
            >
              <Icon icon="mdi:arrow-left" class="h-4 w-4" />
            </button>
            <button
              onclick={() => browse("")}
              disabled={!currentPath}
              class="flex h-8 w-8 items-center justify-center rounded-md transition-colors disabled:opacity-30 text-zinc-400 hover:text-white hover:bg-white/10"
              title={t("storage_page.go_root")}
            >
              <Icon icon="mdi:arrow-up" class="h-4 w-4" />
            </button>
          </div>

          <!-- Breadcrumb path -->
          <div class="flex items-center gap-1 flex-1 min-w-0 overflow-x-auto py-1">
            <button
              onclick={() => browse("")}
              class="flex items-center gap-1.5 rounded-md px-2.5 py-1 text-sm transition-colors shrink-0 hover:bg-white/10 {currentPath ? 'text-zinc-400' : 'text-zinc-100 font-medium'}"
            >
              <Icon icon="streamline:cloud-share" class="h-4 w-4" />
              {t("nav.storage")}
            </button>
            {#each breadcrumbs as segment, i}
              <Icon icon="mdi:chevron-right" class="h-4 w-4 shrink-0 text-zinc-600" />
              <button
                onclick={() => navigateBreadcrumb(i)}
                class="rounded-md px-2.5 py-1 text-sm transition-colors truncate max-w-[120px] sm:max-w-[220px] shrink-0 hover:bg-white/10 {i === breadcrumbs.length - 1 ? 'text-zinc-100 font-medium' : 'text-zinc-400'}"
              >
                {segment}
              </button>
            {/each}
          </div>

          <!-- View mode switcher -->
          <div class="flex items-center shrink-0 ml-2 gap-0.5 rounded-md p-0.5 bg-white/[0.04]">
            <button
              onclick={() => setViewMode("list")}
              class="flex h-7 w-7 items-center justify-center rounded transition-colors {viewMode === 'list' ? 'bg-sky-300/15 text-sky-300 shadow-sm' : 'text-zinc-500 hover:text-zinc-300'}"
              title={t("storage_page.list_view")}
            >
              <Icon icon="mdi:format-list-bulleted" class="h-4 w-4" />
            </button>
            <button
              onclick={() => setViewMode("grid")}
              class="flex h-7 w-7 items-center justify-center rounded transition-colors {viewMode === 'grid' ? 'bg-sky-300/15 text-sky-300 shadow-sm' : 'text-zinc-500 hover:text-zinc-300'}"
              title={t("storage_page.grid_view")}
            >
              <Icon icon="mdi:view-grid-outline" class="h-4 w-4" />
            </button>
          </div>
        </div>

        <!-- Error -->
        {#if error}
          <div class="mb-4 rounded border border-rose-400/30 bg-rose-400/10 px-4 py-3 text-sm text-rose-200 flex items-start gap-2">
            <Icon icon="mdi:alert-circle-outline" class="w-[18px] h-[18px] shrink-0 mt-0.5" />
            <div class="min-w-0">{error}</div>
          </div>
        {/if}

        <!-- File listing -->
        <!-- svelte-ignore a11y_no_static_element_interactions -->
        <div
          class="rounded-xl border transition-colors {dragOver ? 'border-sky-300/35 bg-sky-300/10' : 'border-white/10 bg-white/[0.02]'}"
          ondrop={handleDrop}
          ondragover={handleDragOver}
          ondragleave={() => (dragOver = false)}
        >
        {#if loading || loadingItems}
          <div class="p-8 text-center text-sm text-zinc-400">
            <Icon icon="mdi:loading" class="w-5 h-5 animate-spin inline-block mr-2" />
            {t("common.loading")}
          </div>
        {:else if sortedItems.length === 0}
          <!-- Empty state -->
          <div class="flex flex-col items-center py-16">
            <Icon icon="mdi:folder-open-outline" class="h-12 w-12 mb-3 text-zinc-600" />
            <p class="text-zinc-500">{t("storage_page.empty")}</p>
            <p class="mt-2 text-xs text-zinc-600">{t("storage_page.drop_hint")}</p>
          </div>
        {:else if viewMode === "list"}
          <!-- LIST VIEW -->
          <div class="overflow-hidden">
            <!-- Header -->
            <div
              class="grid gap-4 px-4 py-2.5 text-[11px] font-bold uppercase tracking-widest text-zinc-500 border-b border-white/10 bg-black/20 grid-cols-[1fr_70px_48px] md:grid-cols-[1fr_100px_180px_48px]"
            >
              <span>{t("storage_page.name")}</span>
              <span class="text-right">{t("storage_page.size")}</span>
              <span class="hidden md:block">{t("storage_page.modified")}</span>
              <span></span>
            </div>

            <!-- Rows -->
            {#each sortedItems as item (item.key || item.name)}
              <!-- svelte-ignore a11y_no_static_element_interactions -->
              <div
                class="file-row grid w-full cursor-pointer gap-4 px-4 py-3 text-left transition-colors border-b border-white/5 grid-cols-[1fr_70px_48px] md:grid-cols-[1fr_100px_180px_48px] {drawerItem?.name === item.name && !item.is_dir ? 'bg-sky-300/10 border-sky-300/20' : 'hover:bg-white/[0.03]'}"
                role="button"
                tabindex="0"
                onclick={() => selectItem(item)}
                onkeydown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectItem(item); } }}
              >
                <span class="flex items-center gap-3 min-w-0">
                  <Icon
                    icon={item.is_dir ? "mdi:folder" : fileIcon(item.name)}
                    class="h-5 w-5 shrink-0 {item.is_dir ? 'text-zinc-200' : 'text-zinc-500'}"
                  />
                  <span class="truncate text-sm text-zinc-200">{item.name}{item.is_dir ? "/" : ""}</span>
                </span>

                <span class="text-right text-sm tabular-nums text-zinc-500">
                  {formatSize(item.size)}
                </span>

                <span class="hidden md:block text-xs tabular-nums text-zinc-500">
                  {formatDate(item.last_modified)}
                </span>

                <span class="flex items-center justify-end">
                  {#if !item.is_dir}
                    <button
                      class="delete-btn flex h-7 w-7 items-center justify-center rounded-md transition-colors text-zinc-600 hover:text-rose-400 hover:bg-rose-400/10"
                      onclick={(e) => { e.stopPropagation(); confirmDelete(item); }}
                      title={t("common.delete")}
                    >
                      <Icon icon="mdi:delete-outline" class="h-4 w-4" />
                    </button>
                  {/if}
                </span>
              </div>
            {/each}
          </div>
        {:else}
          <!-- GRID VIEW -->
          <div class="p-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {#each sortedItems as item (item.key || item.name)}
              <button
                class="group flex flex-col items-center gap-2.5 rounded-xl p-4 text-center transition-all border {drawerItem?.name === item.name && !item.is_dir ? 'border-sky-300/20 bg-sky-300/10' : 'border-white/5 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/10'}"
                onclick={() => selectItem(item)}
              >
                {#if !item.is_dir && isImage(item.name)}
                  <div class="w-full aspect-square rounded-lg overflow-hidden bg-black/30">
                    <img
                      src={downloadUrl(item.key || (currentPath ? `${currentPath}/${item.name}` : item.name))}
                      alt={item.name}
                      class="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                {:else}
                  <div class="flex h-14 w-14 items-center justify-center rounded-xl bg-white/[0.04]">
                    <Icon
                      icon={item.is_dir ? "mdi:folder" : fileIcon(item.name)}
                      class="h-7 w-7 {item.is_dir ? 'text-zinc-200' : 'text-zinc-500'}"
                    />
                  </div>
                {/if}
                <span class="w-full truncate text-sm text-zinc-200">{item.name}</span>
                {#if !item.is_dir}
                  <span class="text-xs tabular-nums text-zinc-500">{formatSize(item.size)}</span>
                  <span class="text-[10px] tabular-nums text-zinc-600">{formatDate(item.last_modified)}</span>
                {/if}
              </button>
            {/each}
          </div>
        {/if}
      </div>

        <!-- File count summary -->
        {#if sortedItems.length > 0}
          <div class="mt-3 text-xs text-zinc-500">
            {folderCount} {folderCount === 1 ? t("storage_page.folder") : t("storage_page.folders")}, {fileCount} {fileCount === 1 ? t("storage_page.file") : t("storage_page.files")}
          </div>
        {/if}
      </div>
    </div>

    <!-- DETAIL DRAWER -->
    {#if drawerItem}
      <SlideOver onclose={() => (drawerItem = null)} title={drawerItem.name} width="380px">
        <!-- Image preview -->
        {#if isImage(drawerItem.name)}
          <div class="mb-4">
            <img
              src={downloadUrl(drawerItem.key || (currentPath ? `${currentPath}/${drawerItem.name}` : drawerItem.name))}
              alt={drawerItem.name}
              class="w-full rounded-lg object-contain max-h-60 bg-black/30"
            />
          </div>
        {/if}

        <!-- Details -->
        <div class="space-y-3 mb-6">
          <div class="flex flex-col gap-0.5">
            <span class="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">{t("storage_page.name")}</span>
            <span class="text-sm text-zinc-200 break-all">{drawerItem.name}</span>
          </div>
          <div class="flex flex-col gap-0.5">
            <span class="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">{t("storage_page.path")}</span>
            <span class="text-sm text-zinc-200 font-mono text-xs break-all">{drawerItem.key || (currentPath ? `${currentPath}/${drawerItem.name}` : drawerItem.name)}</span>
          </div>
          <div class="flex flex-col gap-0.5">
            <span class="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">{t("storage_page.size")}</span>
            <span class="text-sm text-zinc-200">{formatSize(drawerItem.size)}</span>
          </div>
          <div class="flex flex-col gap-0.5">
            <span class="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">{t("storage_page.modified")}</span>
            <span class="text-sm text-zinc-200">{formatDate(drawerItem.last_modified)}</span>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex gap-2 border-t border-white/10 pt-5">
          <a
            href={downloadUrl(drawerItem.key || (currentPath ? `${currentPath}/${drawerItem.name}` : drawerItem.name))}
            target="_blank"
            rel="noopener"
            class="ui-btn-primary flex-1 !py-2.5 !text-sm !gap-2 text-center justify-center"
          >
            <Icon icon="mdi:download" class="h-4 w-4" />
            {t("storage_page.download")}
          </a>
          <button
            onclick={() => confirmDelete(drawerItem)}
            class="flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors bg-rose-500 text-white hover:bg-rose-400"
          >
            <Icon icon="mdi:delete-outline" class="h-4 w-4" />
            {t("common.delete")}
          </button>
        </div>
      </SlideOver>
    {/if}
  </div>

  <!-- Delete confirmation modal -->
  {#if deleteTarget}
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div class="fixed inset-0 z-[200] flex items-center justify-center">
      <button
        class="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onclick={() => (deleteTarget = null)}
        aria-label="Close"
      ></button>
      <div class="relative z-10 ui-card p-6 w-full max-w-sm mx-4">
        <div class="flex items-start gap-3 mb-4">
          <div class="flex h-10 w-10 items-center justify-center rounded-full bg-rose-500/10 shrink-0">
            <Icon icon="mdi:delete-outline" class="h-5 w-5 text-rose-400" />
          </div>
          <div>
            <h3 class="text-base font-semibold text-white">{t("storage_page.delete_file")}</h3>
            <p class="mt-1 text-sm text-zinc-400">
              {t("storage_page.delete_confirm").replace("{name}", deleteTarget.name)}
            </p>
          </div>
        </div>
        <div class="flex gap-3 justify-end">
          <button class="ui-btn !px-4 !py-2" onclick={() => (deleteTarget = null)}>{t("common.cancel")}</button>
          <button
            class="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium bg-rose-500 text-white hover:bg-rose-400 transition-colors disabled:opacity-50"
            onclick={handleDelete}
            disabled={deleting}
          >
            {#if deleting}
              <Icon icon="mdi:loading" class="w-4 h-4 animate-spin" />
            {/if}
            {t("common.delete")}
          </button>
        </div>
      </div>
    </div>
  {/if}
</Layout>
